import {
    AuthContext,
    BaseButton,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderDocumentsRequest,
    ProviderDisplayDocumentType,
    ProviderDocument,
    renderDate,
    renderString,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { ClaimStore, IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import { DocumentDetail } from '../claims/components/claim-timeline-table/document-detail';
import * as S from './documents.styles';

interface IDocumentsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IDocumentsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderDocument>[] = [
    {
        id: 'Filename',
        Header: 'Filename',
        accessor: 'filename',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'DateAdded',
        Header: 'Date Added',
        accessor: 'created',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDate(cell.value)}</S.Cell>,
        show: false,
    },
    {
        id: 'Description',
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
        show: false,
    },
    {
        id: 'DocumentType',
        Header: 'Document Type',
        accessor: 'documentType',
        disableSortBy: true,
        Cell: ({ cell }) => (
            <S.Cell>{renderString(cell.value, { type: 'String', normalized: true, formatted: true })}</S.Cell>
        ),
    },
    // {
    //     id: 'ClaimID',
    //     Header: 'Claim',
    //     accessor: 'claimId',
    //     disableSortBy: true,
    //     Cell: ({ cell }) => <S.Cell>{<ClaimIdBadge claimId={cell.value} />}</S.Cell>,
    // },
];

const filterSchemas: IFilterSchema<keyof ListProviderDocumentsRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
    {
        label: 'Document Type',
        filterId: 'filterDocumentType',
        columnId: 'documentType',
        filterType: 'Select',
        options: [
            {
                label: 'Contract',
                value: ProviderDisplayDocumentType.PROVIDERCONTRACT,
            },
            {
                label: 'Report',
                value: ProviderDisplayDocumentType.PROVIDERREPORT,
            },
            {
                label: 'W9',
                value: ProviderDisplayDocumentType.BUSINESSW9,
            },
        ],
    },
    {
        label: 'Filename',
        filterId: 'filterFilename',
        filterType: 'String',
        columnId: 'Filename',
    },
];

@inject('rootStore')
@observer
class DocumentsPage extends React.Component<IDocumentsProps, IDocumentsState> {
    private presenter: DataTablePresenter<ProviderDocument>;

    private get injected(): IInjectedRootStore {
        return this.props as IInjectedRootStore;
    }

    private get claimStore(): ClaimStore {
        return this.injected.rootStore.claimStore;
    }

    constructor(props: IDocumentsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderDocument>({
            name: 'documents',
            data: [],
            columns: [
                ...columns,
                {
                    id: 'Actions',
                    Header: '',
                    // accessor: 'documentType',
                    disableSortBy: true,
                    Cell: ({ cell }) =>
                        cell.row.original.id ? (
                            <DownloadCell id={cell.row.original.id} claimStore={this.claimStore} />
                        ) : null,
                },
            ],
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'Provider Dashboard - Documents';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('documents');
    }

    public render() {
        return (
            <PageLayout>
                <PageHeader title="Documents" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            onRowClick={this.onRowClick}
                            renderRowSubComponent={this.renderRowSubComponent}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onRowClick = async (row: Row<ProviderDocument>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderDocument>): React.ReactElement => {
        return (
            <S.Expand>
                <DocumentDetail document={row.original} />
            </S.Expand>
        );
    };

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('documents');
            this.authContext.setGroup('documents');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, columns);
            const listing = await this.props.rootStore.documentStore.loadDocumentListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };
}

export const Documents: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <DocumentsPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);

export const DownloadCell: React.FunctionComponent<{ id: string; claimStore: ClaimStore }> = ({ id, claimStore }) => {
    const [loading, setLoading] = React.useState(false);

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setLoading(true);
        try {
            const docUrl = await claimStore.loadDocumentDownloadUrl(id);
            if (docUrl && docUrl.url) {
                window.open(docUrl.url, '_blank');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <S.CellAlignedRight>
            <BaseButton variant="primary" onClick={handleDownload} disabled={loading}>
                <span>Download</span>
            </BaseButton>
        </S.CellAlignedRight>
    );
};
